/* eslint-disable react/jsx-no-bind */
/* eslint-disable id-length */
import { memo } from "react";
import { Node, NodeProps } from "@xyflow/react";
import {
  CommandStep,
  isDefaultCommandStep,
  isMatrixCommandStep,
  isParallelCommandStep,
  isWaitingOnConcurrencyGroup,
  isWaitingOnPlatformLimit,
} from "~/lib/pipeline";
import BaseNode from "~/components/Playground/Node/BaseNode";
import Commands from "~/components/Playground/Node/CommandStepNode/Commands";
import Plugins from "~/components/Playground/Node/CommandStepNode/Plugins";
import ArtifactPaths from "~/components/Playground/Node/CommandStepNode/ArtifactPaths";
import { getDockerPlugin } from "~/lib/pipeline/plugins";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import {
  useBuild,
  useMostRecentMatrixJobsForStep,
  useMostRecentParallelJobsForStep,
} from "~/components/build/Show/lib/BuildContext";
import { StateIcon, StateLabel } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import { Jobs } from "~/components/Playground/Node/Jobs";
import { CommandJob } from "~/components/build/Show/lib/types";
import { JobDuration } from "~/components/job/Job/JobDuration";
import { MissingStepDependencies } from "~/components/Playground/Node/MissingStepDependencies";
import { Link } from "react-router-dom";
import CommandStepLabel from "~/components/build/Show/components/CommandStepLabel";
import { JobCount, StatsJobCount } from "~/components/build/Show/components/StatsJobCount";
import { ParallelJobs } from "~/components/Playground/Node/CommandStepNode/ParallelJobs";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { NodeDuration } from "~/components/Playground/Node/NodeDuration";
import { stepJobsQueryOptions } from "~/components/build/Show/lib/queries/stepJobs";
import { useQuery } from "@tanstack/react-query";
import { Build } from "~/components/build/Show/lib/types/Build";

type CommandStepNodeType = Node<CommandStep, "command">;

function useCommandJobs(step: CommandStep): CommandJob[] {
  const { build } = useBuild();
  const eagerLoadedJobs = build.jobs.filter((job) => job.stepUuid === step.uuid) as CommandJob[];

  const { data: lazilyLoadedJobs } = useQuery(
    stepJobsQueryOptions(build, step.uuid, { enabled: Features.BuildSidebar }),
  );

  const defaultJobs = (Features.BuildSidebar ? lazilyLoadedJobs : eagerLoadedJobs) ?? [];

  const parallelJobs = useMostRecentParallelJobsForStep(step.uuid);
  const matrixJobs = useMostRecentMatrixJobsForStep(step.uuid);

  let jobs: CommandJob[];
  if (isParallelCommandStep(step)) {
    jobs = parallelJobs;
  } else if (isMatrixCommandStep(step)) {
    jobs = matrixJobs;
  } else {
    jobs = defaultJobs as CommandJob[];
  }

  return jobs;
}

/**
 * Deferred hover content that fetches job data only when mounted.
 * Since this is rendered inside HoverCard.Portal (which lazy-mounts on hover),
 * the fetch only fires when the user actually hovers over the node.
 */
function CommandStepHoverContent({
  data,
  build,
  missingDependencies,
}: {
  data: CommandStep;
  build: Build;
  missingDependencies: string[];
}) {
  const jobs = useCommandJobs(data);

  const latestJob = jobs.length ? jobs[jobs.length - 1] : null;

  const { plugins } = data;
  const docker = plugins ? getDockerPlugin(plugins) : null;
  const waitingOnConcurrencyGroup = isWaitingOnConcurrencyGroup(latestJob?.state, data.statistics);
  const waitingOnPlatformLimit = isWaitingOnPlatformLimit(latestJob?.state, data.statistics);

  const label = <CommandStepLabel {...data} />;

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {data.label && <div className="line-clamp-2 break-words font-medium">{label}</div>}

        <div className="flex items-baseline gap-2">
          {isDefaultCommandStep(data) && latestJob && (
            <JobDuration job={latestJob} build={build} className="text-charcoal-300" />
          )}

          {(isParallelCommandStep(data) || isMatrixCommandStep(data)) && (
            <Jobs jobs={jobs} step={data} />
          )}

          <div className="ml-auto flex items-center gap-2">
            <StateLabel {...data} missingDependencies={missingDependencies} />
            <StateIcon
              className={textColorForStep({
                ...data,
                missingDependencies,
                waitingOnConcurrencyGroup,
                waitingOnPlatformLimit,
              })}
              {...data}
              missingDependencies={missingDependencies}
              waitingOnConcurrencyGroup={waitingOnConcurrencyGroup}
              waitingOnPlatformLimit={waitingOnPlatformLimit}
            />
          </div>
        </div>
      </div>

      <MissingStepDependencies missingDependencies={missingDependencies} build={build} />

      <Commands data={data} dockerImage={docker?.image} />

      {!Features.BuildSidebar && isDefaultCommandStep(data) && latestJob && (
        <div>
          <Link
            to={latestJob.path}
            onClick={(e) => e.stopPropagation()}
            className="btn-depth btn-small p-2 no-underline hover:no-underline"
          >
            Go to job
          </Link>
        </div>
      )}
    </div>
  );
}

function JobsCountWrapper(props: { step: CommandStep }) {
  const jobs = useCommandJobs(props.step);

  return <JobCount jobs={jobs} step={props.step} />;
}

function CommandStepNode(props: NodeProps<CommandStepNodeType>) {
  const { build } = useBuild({ strict: false });
  const { missingDependencies } = useStepDependencies(props.id);
  const annotationCountsByStyle = props.data.statistics?.annotationCountsByStyle;

  const { plugins, artifactPaths } = props.data;
  const docker = plugins ? getDockerPlugin(plugins) : null;

  const label = <CommandStepLabel {...props.data} />;

  const duration =
    Features.BuildCanvasDurations &&
    Features.BuildSidebar &&
    props.data.state &&
    isDefaultCommandStep(props.data) ? (
      <NodeDuration
        startedAt={props.data.statistics?.latestJobStartedAt}
        finishedAt={props.data.statistics?.latestJobFinishedAt}
      />
    ) : undefined;

  return (
    <HoverCard.Root>
      <HoverCard.Portal container={document.getElementById("canvas")}>
        {build && props.data.state && (
          <HoverCard.Content
            state={props.data.state}
            outcome={props.data.outcome}
            missingDependencies={missingDependencies}
            collisionBoundary={document.getElementById("canvas")}
            collisionPadding={10}
          >
            <CommandStepHoverContent
              data={props.data}
              build={build}
              missingDependencies={missingDependencies}
            />
          </HoverCard.Content>
        )}
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>
        <div className="relative">
          <BaseNode
            {...props}
            label={label}
            duration={duration}
            annotationCountsByStyle={annotationCountsByStyle}
            count={
              (isParallelCommandStep(props.data) || isMatrixCommandStep(props.data)) &&
              (Features.BuildSidebar ? (
                <StatsJobCount step={props.data} />
              ) : (
                <JobsCountWrapper step={props.data} />
              ))
            }
          >
            {build && Features.BuildCanvasParallelJobs && isParallelCommandStep(props.data) && (
              <ParallelJobs step={props.data} />
            )}
            {!props.data.state && <Commands data={props.data} dockerImage={docker?.image} />}
            {plugins && <Plugins plugins={plugins} />}
            {artifactPaths && <ArtifactPaths paths={artifactPaths} />}
          </BaseNode>
        </div>
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
}

export default memo(CommandStepNode);
